import React, {
    useContext, useState, useCallback, useRef, useMemo, useEffect,
} from 'react';
import { Portal } from 'react-portal';

import { ConfigContext } from '../ConfigProvider';

import { navigationConfig, findTargetByNavigationItem } from './navigationItems';
import { scrollPageToElement } from './navigationAnimate';
import { observePageScroll } from './navigationObserveScroll';
import { NavigationButton } from './NavigationButton';

import css from './navigation.module.scss';


function Navigation() {
    const { pageId } = useContext(ConfigContext);
    const navigationItems = useMemo(() => navigationConfig[pageId], [pageId]);

    const defaultActiveItemId = navigationItems ? navigationItems[0].id : '';
    const [activeItemId, setActiveItemId] = useState(defaultActiveItemId);

    const isAnimatingRef = useRef(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const handleSelectItem = useCallback(async (itemId: string) => {
        setActiveItemId(itemId);

        const navigationItem = navigationItems.find((item) => item.id === itemId);

        if (navigationItem) {
            const target = findTargetByNavigationItem(navigationItem);
            if (target) {
                isAnimatingRef.current = true;
                await scrollPageToElement(target);
                isAnimatingRef.current = false;
            }
        }
    }, [navigationItems]);


    useEffect(() => {
        const observer = observePageScroll((navigationItemId: string) => {
            if (isAnimatingRef.current) return;

            const matchingNavigationItem = navigationItems.find((item) => item.id === navigationItemId);
            if (matchingNavigationItem) {
                setActiveItemId(navigationItemId);
            }
        });

        return () => observer.disconnect();
    }, [navigationItems]);


    if (!navigationItems) return null;

    return (
        <div
            className={css.navigationContainer}
            ref={containerRef}
        >
            <div
                className={css.navigationList}
                ref={listRef}
            >
                {navigationItems.map((item) => (
                    <NavigationButton
                        key={item.id}
                        item={item}
                        isActive={activeItemId === item.id}
                        containerRef={containerRef}
                        scrollingListRef={listRef}
                        onClick={handleSelectItem}
                    />
                ))}
            </div>
        </div>
    );
}


export function NavigationPortal() {
    const navRootNode = document.querySelector('[data-navigation-container]');

    if (!navRootNode) return null;

    return (
        <Portal node={navRootNode}>
            <Navigation />
        </Portal>
    );
}
